import customAxios from '@/axios/config'

export default {
  namespaced: true,

  state: {
    channelListings: [],
    availableListings: [],
    ratePlans: [],
    importedRatePlanCodes: [],
    connectionStatus: {
      success: false,
      message: '',
    },
    loading: {
      channelListings: false,
      testConnection: false,
      syncListings: false,
      syncReservations: false,
      pushAvailability: false,
      availableListings: false,
      createListings: false,
      ratePlans: false,
      importRatePlans: false,
    },
  },

  mutations: {
    setChannelListings(state, listings) {
      state.channelListings = listings
    },

    setAvailableListings(state, listings) {
      state.availableListings = listings
    },

    setRatePlans(state, ratePlans) {
      state.ratePlans = ratePlans
    },

    setImportedRatePlanCodes(state, codes) {
      state.importedRatePlanCodes = codes
    },

    setConnectionStatus(state, status) {
      state.connectionStatus = status
    },

    setLoading(state, { key, value }) {
      state.loading[key] = value
    },

    updateChannelListing(state, updatedListing) {
      const index = state.channelListings.findIndex(
        cl => cl.id === updatedListing.id
      )
      if (index !== -1) {
        state.channelListings.splice(index, 1, updatedListing)
      }
    },

    addChannelListing(state, newListing) {
      state.channelListings.push(newListing)
    },

    removeChannelListing(state, listingId) {
      const index = state.channelListings.findIndex(cl => cl.id === listingId)
      if (index !== -1) {
        state.channelListings.splice(index, 1)
      }
    },
  },

  actions: {
    async fetchChannelListings({ commit }) {
      commit('setLoading', { key: 'channelListings', value: true })
      try {
        const response = await customAxios.get(
          '/api/simple-booking/channel-listings'
        )
        commit('setChannelListings', response.data)
        return response.data
      } catch (error) {
        console.error('Error fetching channel listings:', error)
        throw error
      } finally {
        commit('setLoading', { key: 'channelListings', value: false })
      }
    },

    async fetchAvailableListings({ commit }) {
      commit('setLoading', { key: 'availableListings', value: true })
      try {
        const response = await customAxios.get('/api/listings-picker')
        const listings = response.data || []

        const processedListings = listings
          .filter(listing => listing.listed)
          .map(listing => ({
            ...listing,
            display_name:
              listing.nickname || listing.title || `Listing #${listing.id}`,
          }))

        commit('setAvailableListings', processedListings)
        return processedListings
      } catch (error) {
        console.error('Error fetching available listings:', error)
        commit('setAvailableListings', [])
        throw error
      } finally {
        commit('setLoading', { key: 'availableListings', value: false })
      }
    },

    async testConnection({ commit }) {
      commit('setLoading', { key: 'testConnection', value: true })
      try {
        const response = await customAxios.get(
          '/api/simple-booking/test-connection'
        )
        const status = {
          success: response.data.success,
          message: response.data.message,
        }
        commit('setConnectionStatus', status)
        return status
      } catch (error) {
        const status = {
          success: false,
          message: error.response?.data?.message || 'Connection test failed',
        }
        commit('setConnectionStatus', status)
        throw error
      } finally {
        commit('setLoading', { key: 'testConnection', value: false })
      }
    },

    async syncListings({ commit, dispatch }) {
      commit('setLoading', { key: 'syncListings', value: true })
      try {
        await customAxios.post('/api/simple-booking/sync-listings')
        setTimeout(() => dispatch('fetchChannelListings'), 3000)
        return { success: true }
      } catch (error) {
        console.error('Error syncing listings:', error)
        throw error
      } finally {
        commit('setLoading', { key: 'syncListings', value: false })
      }
    },

    async syncReservations({ commit }, { fromDate, toDate } = {}) {
      commit('setLoading', { key: 'syncReservations', value: true })
      try {
        const params = {}
        if (fromDate) params.from_date = fromDate
        if (toDate) params.to_date = toDate

        await customAxios.post('/api/simple-booking/sync-reservations', params)
        return { success: true }
      } catch (error) {
        console.error('Error syncing reservations:', error)
        throw error
      } finally {
        commit('setLoading', { key: 'syncReservations', value: false })
      }
    },

    async pushAvailability({ commit, state }, { pushRates = false } = {}) {
      commit('setLoading', { key: 'pushAvailability', value: true })
      try {
        const linkedListings = state.channelListings.filter(cl => cl.listing_id)

        if (linkedListings.length === 0) {
          throw new Error('No linked listings to push availability for')
        }

        const promises = linkedListings.map(cl =>
          customAxios.post('/api/simple-booking/push-availability', {
            listing_id: cl.listing_id,
            push_rates: pushRates,
          })
        )

        await Promise.all(promises)
        return {
          success: true,
          message: `Queued availability push for ${linkedListings.length} listings`,
        }
      } catch (error) {
        console.error('Error pushing availability:', error)
        throw error
      } finally {
        commit('setLoading', { key: 'pushAvailability', value: false })
      }
    },

    async linkChannelListing(
      { commit },
      { channelListingId, listingId, ratePlanCode, rateTier }
    ) {
      try {
        const response = await customAxios.patch(
          `/api/simple-booking/channel-listings/${channelListingId}`,
          {
            listing_id: listingId,
            rate_plan_code: ratePlanCode,
            rate_tier: rateTier,
          }
        )
        return response.data
      } catch (error) {
        console.error('Error linking listing:', error)
        throw error
      }
    },

    async unlinkChannelListing({ commit }, { channelListingId }) {
      try {
        const response = await customAxios.patch(
          `/api/simple-booking/channel-listings/${channelListingId}`,
          {
            listing_id: null,
          }
        )
        return response.data
      } catch (error) {
        console.error('Error unlinking listing:', error)
        throw error
      }
    },

    async createChannelListings({ commit, state, dispatch }, { listingIds }) {
      commit('setLoading', { key: 'createListings', value: true })
      try {
        const promises = listingIds.map(listingId => {
          const listing = state.availableListings.find(l => l.id === listingId)
          return customAxios.post(
            '/api/simple-booking/create-channel-listing',
            {
              listing_id: listingId,
              external_listing_id: `LISTING_${listingId}`,
              name: listing.nickname || listing.title,
            }
          )
        })

        await Promise.all(promises)

        dispatch('fetchChannelListings')
        dispatch('fetchAvailableListings')

        return {
          success: true,
          count: listingIds.length,
        }
      } catch (error) {
        console.error('Error creating channel listings:', error)
        throw error
      } finally {
        commit('setLoading', { key: 'createListings', value: false })
      }
    },

    async linkExistingChannelListing(
      { commit },
      { externalListingId, listingId, ratePlanCode, rateTier }
    ) {
      try {
        const response = await customAxios.post(
          '/api/simple-booking/link-channel-listing',
          {
            external_listing_id: externalListingId,
            listing_id: listingId,
            rate_plan_code: ratePlanCode,
            rate_tier: rateTier,
          }
        )
        return response.data
      } catch (error) {
        console.error('Error linking existing channel listing:', error)
        throw error
      }
    },

    async fetchRatePlans({ commit }) {
      commit('setLoading', { key: 'ratePlans', value: true })
      try {
        const response = await customAxios.get('/api/simple-booking/rate-plans')
        commit('setRatePlans', response.data)
        // also fetch imported codes for UI
        try {
          const db = await customAxios.get('/api/rate-plans', {
            params: { pms_type: 'simple_booking' },
          })
          const codes = (db.data?.payload?.rate_plans || []).map(rp => rp.code)
          commit('setImportedRatePlanCodes', codes)
        } catch (e) {
          commit('setImportedRatePlanCodes', [])
        }
        return response.data
      } catch (error) {
        console.error('Error fetching rate plans:', error)
        throw error
      } finally {
        commit('setLoading', { key: 'ratePlans', value: false })
      }
    },

    async importRatePlansToDb({ state, commit }) {
      commit('setLoading', { key: 'importRatePlans', value: true })
      try {
        const response = await customAxios.post('/api/simple-booking/map-rates')

        // refresh imported codes after import
        try {
          const db = await customAxios.get('/api/rate-plans', {
            params: { pms_type: 'simple_booking' },
          })
          const codes = (db.data?.payload?.rate_plans || []).map(rp => rp.code)
          commit('setImportedRatePlanCodes', codes)
        } catch (e) {
          // noop
        }

        return response.data
      } catch (error) {
        console.error('Error importing rate plans:', error)
        throw error
      } finally {
        commit('setLoading', { key: 'importRatePlans', value: false })
      }
    },

    async refreshData({ dispatch }) {
      await Promise.all([
        dispatch('fetchChannelListings'),
        dispatch('fetchAvailableListings'),
        dispatch('fetchRatePlans'),
      ])
    },
  },

  getters: {
    unlinkedListings(state) {
      const linkedListingIds = state.channelListings
        .filter(cl => cl.listing_id)
        .map(cl => cl.listing_id)

      return state.availableListings.filter(
        listing => !linkedListingIds.includes(listing.id)
      )
    },

    isLoading(state) {
      return key => state.loading[key] || false
    },
  },
}
